import { nextTick } from '@vue/composition-api';
import { defineStore } from 'pinia';
import {
	getCommunity,
	updateCommunity,
	addGroupsToCommunity,
	removeGroupsFromCommunity,
	updateCommunityOrganization,
	updateCommunityAdministrators,
} from '@front-app/services/CommunityApi/CommunityApi';
import {
	CommunityWithGroups,
	CreateCommunityRequest,
	UpdateCommunityOrganizationRequest,
} from '@front-app/services/CommunityApi/CommunityModel';
import { isAxiosError } from '@front-app/util/axios';

interface CommunitiesStoreState {
	communities: CommunityWithGroups[];
}

export const useCommunitiesStore = defineStore('communitiesStore', {
	state: (): CommunitiesStoreState => ({
		communities: [],
	}),
	getters: {
		getById(state) {
			return (communityId: string) => state.communities.find(({ id }) => id === communityId);
		},
	},
	actions: {
		update(newCommunities: CommunityWithGroups[]) {
			this.$patch((state) => {
				const communityIds = newCommunities.map(({ id }) => id);
				const oldCommunities = state.communities.filter(({ id }) => !communityIds.includes(id));
				state.communities = oldCommunities.concat(newCommunities);
			});
		},
		async fetchById(communityId: string) {
			const community = await getCommunity(communityId);
			this.update([community]);
			await nextTick();
		},
		async saveCommunityUpdates(communityId: string, updatedCommunity: Partial<CreateCommunityRequest>) {
			const original = this.getById(communityId);
			if (!original) {
				throw new Error('Cannot update non-existent community. Please make sure to fetch community before updating it.');
			}
			const existingState: CreateCommunityRequest = {
				name: original.name,
				customColorPalette: original.customColorPalette,
				isActive: original.isActive,
			};
			const community = await updateCommunity(communityId, { ...existingState, ...updatedCommunity });
			this.update([community]);
		},
		async saveOrganization(communityId: string, updates: UpdateCommunityOrganizationRequest) {
			const community = await updateCommunityOrganization(communityId, updates);
			this.update([community]);
		},
		async saveAdministrators(communityId: string, updates: UpdateCommunityOrganizationRequest) {
			const community = await updateCommunityAdministrators(communityId, updates);
			this.update([community]);
		},
		async addCommunityGroups(communityId: string, params: string[], callback: () => void) {
			try {
				const community = await addGroupsToCommunity(communityId, params);
				this.update([community]);
			} catch (e: unknown) {
				if (isAxiosError(e) && e.response?.status === 409) {
					callback();
					return Promise.reject(e);
				} else {
					throw e;
				}
			}
		},
		async removeCommunityGroups(communityId: string, params: string[]) {
			const community = await removeGroupsFromCommunity(communityId, params);
			this.update([community]);
		},
	},
});
